import { useDebugStore } from '@/stores/debugStore';

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

function isDebug(): boolean {
    return useDebugStore().enabled;
}

export default function useChatService() {
    return {
        /**
         * Conversation history is held server-side against the caller's thread,
         * so a prompt is all this sends — the assistant recovers the context
         * itself.
         */
        streamChat(
            orgPublicId: string,
            prompt: string,
            onChunk: (text: string) => void,
            onDone: () => void,
            onError: (error: string) => void,
            helpKey?: string | null,
        ): AbortController {
            const controller = new AbortController();
            const debug = isDebug();

            const xsrfToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            if (debug) {
                console.log('[ChatService] Starting stream request', {
                    url: `/api/organisations/${orgPublicId}/chat/stream`,
                    prompt,
                });
            }

            fetch(`/api/organisations/${orgPublicId}/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    'X-XSRF-TOKEN': xsrfToken ? decodeURIComponent(xsrfToken) : '',
                },
                credentials: 'same-origin',
                signal: controller.signal,
                body: JSON.stringify({ prompt, help_key: helpKey ?? null }),
            })
                .then(async (response) => {
                    if (debug) {
                        console.log('[ChatService] Response received', {
                            status: response.status,
                            statusText: response.statusText,
                            contentType: response.headers.get('content-type'),
                        });
                    }

                    if (!response.ok) {
                        const text = await response.text();
                        if (debug) {
                            console.error('[ChatService] Response not OK', {
                                status: response.status,
                                body: text,
                            });
                        }

                        let errorMessage = `Request failed (${response.status})`;
                        try {
                            const json = JSON.parse(text);
                            errorMessage = json.message || errorMessage;
                        } catch {
                            // Response wasn't JSON — likely an HTML error page
                        }

                        onError(errorMessage);
                        return;
                    }

                    const reader = response.body?.getReader();
                    if (!reader) {
                        onError('No response body');
                        return;
                    }

                    const decoder = new TextDecoder();
                    let buffer = '';
                    let chunkCount = 0;

                    while (true) {
                        // eslint-disable-next-line no-await-in-loop
                        const { done, value } = await reader.read();
                        if (done) {
                            if (debug) {
                                console.log('[ChatService] Stream finished', { totalChunks: chunkCount });
                            }
                            break;
                        }

                        const rawChunk = decoder.decode(value, { stream: true });
                        if (debug) {
                            console.log('[ChatService] Raw chunk received', {
                                chunkNumber: ++chunkCount,
                                rawLength: rawChunk.length,
                                rawContent: rawChunk.substring(0, 500),
                            });
                        }

                        buffer += rawChunk;
                        const lines = buffer.split('\n');
                        buffer = lines.pop() ?? '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                const data = line.slice(6);

                                if (data === '[DONE]') {
                                    continue;
                                }
                                try {
                                    const parsed = JSON.parse(data);
                                    if (debug) {
                                        console.log('[ChatService] Parsed SSE event', {
                                            type: parsed.type,
                                            keys: Object.keys(parsed),
                                            delta: parsed.delta?.substring?.(0, 100),
                                            text: parsed.text?.substring?.(0, 100),
                                        });
                                    }

                                    if (parsed.error) {
                                        onError(parsed.error);
                                        return;
                                    } else if (parsed.text) {
                                        onChunk(parsed.text);
                                    } else if (parsed.delta) {
                                        onChunk(parsed.delta);
                                    }
                                } catch {
                                    if (data.trim()) {
                                        onChunk(data);
                                    }
                                }
                            }
                        }
                    }

                    onDone();
                })
                .catch((err) => {
                    if (err.name !== 'AbortError') {
                        if (debug) {
                            console.error('[ChatService] Stream error', {
                                name: err.name,
                                message: err.message,
                            });
                        }
                        onError(err.message || 'Stream failed');
                    }
                });

            return controller;
        },
    };
}
